import { useCallback } from 'react';
import { useQueryClient } from 'react-query';

import { type IConversation } from 'src/api/landlord-messages-api';
import { type IUser } from 'src/api/users-api';
import { type ILead, queryKeys } from 'src/services/api';
import { fetchLeadMessages } from 'src/services/api/messages/fetch-lead-messages';

export const useUpdateConversations = () => {
    const queryClient = useQueryClient();

    const updateConversations = useCallback(async (updatedConversations: IConversation[]) => {
        const loggedInUser = queryClient.getQueryData<IUser>(queryKeys.loggedInUser);
        const leads = queryClient.getQueryData<ILead[]>(queryKeys.leads);

        if (!loggedInUser || !leads) {
            return;
        }
        // New async function for a single conversation
        const updateAndDisplayNotification = async (updatedConversation: IConversation) => {
            const leadUuid = updatedConversation.lead_uuid;
            await fetchLeadMessages(leadUuid)
                .then(_messages => {
                    return _messages;
                })
                .catch(error => {
                    console.error('Failed to fetch messages:', error);
                });

            // Update the conversation in the query client one by one
            queryClient.setQueryData([
                queryKeys.conversations,
                'lead',
                updatedConversation.lead_uuid,
            ], updatedConversation);
        };

        // Run all updates in parallel
        await Promise.all(updatedConversations.map(updateAndDisplayNotification));

        // Update the conversations list in the query client
        // IB: Not why we are doing that again, but who knows, maybe we need to update the list with new conversations
        // This will ensure that the conversations list is up-to-date with the latest changes
        queryClient.setQueryData(queryKeys.conversations, (oldData: IConversation[] | undefined) => {
            if (!oldData) {
                return updatedConversations;
            }
            const updatedConversationsByLeadUuid = new Map(updatedConversations.map((o) => [o.lead_uuid, o]));
            const filtered = oldData.filter((conv) => !updatedConversationsByLeadUuid.has(conv.lead_uuid));
            return [...filtered, ...updatedConversationsByLeadUuid.values()];
        });
    }, [queryClient]);

    return updateConversations;
};
